import { useCallback, useState } from "react";
import { useFetch } from "modules/main/hooks/useFetch";
import { settingsLocationEndPoint } from "modules/main/services/endpoints";
import type { LocationResponse } from "modules/settings/types/location-types";
import {
  LocationSelectorFields,
  type LocationSelectorFieldsChangePayload,
  type LocationSelectorFieldsValue,
} from "modules/main/components/location-selector-fields";

/**
 * Draft selection reported from step 2 to the parent page.
 *
 * Both ids are strings because Radix Select is string-based. The parent
 * converts them to numbers and persists the typed `OnboardingState`.
 */
export interface StepTwoSelection {
  cityId: string;
  cityName: string;
  locationId: string;
  locationName: string;
  isFallback: boolean;
}

export interface StepTwoProps {
  /** Called whenever the user picks a comuna or a sector. */
  onSelectionChange: (selection: StepTwoSelection) => void;
  /** Optional pre-selected value (used to rehydrate from a parent draft). */
  value?: StepTwoSelection;
  /** Optional inline error rendered under the selectors. */
  error?: string;
}

/** Shared request options for the GET /settings/locations fetch. */
const LOCATION_REQUEST_OPTIONS = {
  method: "GET" as const,
  headers: {},
  credentials: "include" as const,
};

/**
 * Step 2 of the onboarding wizard — comuna + sector selector.
 *
 * Uses the shared `LocationSelectorFields` presentational component in
 * external-submit mode (no submit button, no `updateLocationEndPoint` POST,
 * no `localStorage.userData` write). The parent page owns the draft state,
 * validates it on Siguiente, and persists a typed `OnboardingState` to
 * `localStorage["carriapp:onboarding"]`.
 */
const StepTwo = ({ onSelectionChange, value, error }: StepTwoProps) => {
  const { data, loading } = useFetch<LocationResponse>(
    settingsLocationEndPoint,
    LOCATION_REQUEST_OPTIONS,
  );

  const [internalValue, setInternalValue] = useState<LocationSelectorFieldsValue>(
    () => ({
      cityId: value?.cityId ?? "",
      locationId: value?.locationId ?? "",
    }),
  );

  const handleSelectionChange = useCallback(
    (payload: LocationSelectorFieldsChangePayload) => {
      setInternalValue({
        cityId: payload.cityId,
        locationId: payload.locationId,
      });
      onSelectionChange({
        cityId: payload.cityId,
        cityName: payload.cityName,
        locationId: payload.locationId,
        locationName: payload.locationName,
        isFallback: payload.isFallback,
      });
    },
    [onSelectionChange],
  );

  return (
    <section className="flex flex-col gap-4" aria-label="Paso 2">
      <h1 className="text-2xl font-semibold leading-tight text-foreground">
        Elige tu ubicación
      </h1>
      <p className="text-muted-foreground leading-relaxed">
        Selecciona tu comuna y sector para mostrarte precios, stock y despacho
        de los supermercados disponibles cerca de ti.
      </p>
      <LocationSelectorFields
        cities={data?.cities ?? []}
        locations={data?.locations ?? []}
        loading={loading}
        value={internalValue}
        onChange={handleSelectionChange}
        includeFallback
        error={error}
      />
    </section>
  );
};

export default StepTwo;
